
import { computed, defineComponent, onMounted, PropType, ref } from 'vue';
import { initializeStore, SeamlessCoreStore } from '@seamless/store';
import { initializeComponentRegistry } from '@owc/component-registry-connection';
import { SliderComponent } from '@workbench/core/dist/types/components/slider/slider';
import { getWebcomponentRootFromElement } from './utils/webcomponent';
import { useAutoRotation } from './composables/auto-rotation.composable';
import { isTrue } from '@dh-io-emh/stage-campaign-item/src/utils/AEM';
import { WorkbenchTheme } from '@workbench/core/dist/types/shared/themes';

const STAGE_CAMPAIGN_ITEM_TAG = 'emh-stage-campaign-item';
const HOST_CSS_CLASS = 'emh-stage-campaign';
const MAX_STAGE_CAMPAIGN_ITEMS = 5;
const AUTO_ROTATION_INTERVAL = 7000;
const STAGE_CAMPAIGN_ITEM_SLOT = '[slot=emh-stage-campaign-items]';

interface HTMLElementWithDatajson extends HTMLElement {
  datajson: string;
  'component-id': string;
}

export default defineComponent({
  name: 'EmhStageCampaign',
  props: {
    store: {
      type: Object as PropType<SeamlessCoreStore>,
      default: () => initializeStore(),
    },
  },
  setup() {
    const theme = ref<WorkbenchTheme>('dark');
    const hostRef = ref<HTMLElement>();
    const sliderRef = ref<(HTMLElement & SliderComponent) | null>(null);
    const mutationObserver = ref<MutationObserver>();
    const dir = ref('');
    const itemsDatajson = ref<unknown[]>([]);

    onMounted(() => {
      setupMutationObserver();
      setupCarouselOverflowWorkaround();
      updateDirAttribute();
    });

    const limitedItemsDatajson = computed(() => {
      const notHiddenItems = itemsDatajson.value.filter((item) => !isTrue(JSON.parse(item as string).payload.hide));

      return notHiddenItems.slice(0, MAX_STAGE_CAMPAIGN_ITEMS);
    });

    const { paused, startRotation, stopRotation } = useAutoRotation(
      sliderRef,
      limitedItemsDatajson,
      AUTO_ROTATION_INTERVAL,
    );

    const setupMutationObserver = () => {
      if (!hostRef.value) {
        console.log('hostRef not initialized');
      }

      mutationObserver.value = new MutationObserver(getDataJsonFromCampaignItems);

      const slot = hostRef.value?.querySelector(STAGE_CAMPAIGN_ITEM_SLOT);

      if (slot) {
        mutationObserver.value.observe(slot, { childList: false, attributes: true, subtree: true });
      } else {
        console.log('Slot does not exist');
      }

      // Recheck after all elements, scripts and images are loaded (sometimes the observable is failing)
      window.addEventListener('DOMContentLoaded', async () => {
        await getDataJsonFromCampaignItems();
        applyClassesToHost();
      });
    };

    const applyClassesToHost = () => {
      if (!hostRef.value) {
        return;
      }

      const host = getWebcomponentRootFromElement(hostRef.value);

      if (!host) {
        return;
      }

      if (limitedItemsDatajson.value.length < 1) {
        host.classList.add('all-hidden');
      }

      if (host.classList.contains(HOST_CSS_CLASS)) {
        return;
      }

      host.classList.add(HOST_CSS_CLASS);
    };

    const updateDirAttribute = () => {
      const html = document.querySelector('html');

      if (!html) {
        return;
      }

      dir.value = html.getAttribute('dir') ?? '';
    };

    const setupCarouselOverflowWorkaround = () => {
      const rootElem = document.querySelector<HTMLDivElement>('.root');

      if (rootElem) {
        rootElem.style.overflow = 'hidden';
      }
    };

    const getDataJsonFromCampaignItems = async () => {
      const root = getWebcomponentRootFromElement(hostRef.value);
      const slot = root?.querySelector<HTMLDivElement>(STAGE_CAMPAIGN_ITEM_SLOT);

      if (!slot) {
        return;
      }

      const stageItems = Array.from(slot.querySelectorAll<HTMLElementWithDatajson>(STAGE_CAMPAIGN_ITEM_TAG));

      itemsDatajson.value = await Promise.all(
        stageItems
          .filter((item) => item.datajson || item.getAttribute('component-id'))
          .map((item) => {
            if (item.datajson) {
              return Promise.resolve(item.datajson);
            } else if (item.getAttribute('component-id')) {
              const componentId = item.getAttribute('component-id');

              return new Promise((resolve) => {
                const handler = (data: unknown) => {
                  resolve(JSON.stringify(data));
                  setTimeout(() => {
                    componentRegistry.unsubscribe(componentId as string, handler);
                  }, 0);
                };
                const componentRegistry = initializeComponentRegistry();

                componentRegistry.subscribe(componentId as string, handler);
              });
            }
          }),
      );

      startRotation();
    };

    const onItemThemeChange = (event: CustomEvent<WorkbenchTheme[]>) => {
      if (!sliderRef.value) {
        return;
      }

      sliderRef.value.theme = event.detail[0];
    };

    const onPopupsOpen = () => {
      stopRotation();
    };

    const onPopupsClose = () => {
      startRotation();
    };

    return {
      paused,
      AUTO_ROTATION_INTERVAL,
      sliderRef,
      hostRef,
      dir,
      itemsDatajson,
      limitedItemsDatajson,
      theme,
      onItemThemeChange,
      onPopupsOpen,
      onPopupsClose,
    };
  },
});
